/**
 * TabPlaceholder — F12.D T12.D.10.
 *
 * Komponent „Wkrótce" dla zakładek w `ModuleSettingsPage` które jeszcze
 * nie mają implementacji. Pokazuje user-facing info o tym w której fazie
 * F12.X dany feature się pojawi.
 *
 * Pattern roadmapy w UI — zamiast pustej zakładki user widzi:
 *  - co tu będzie (feature description)
 *  - kiedy to przyjdzie (faza F12.X)
 *  - link do roadmapy lub do implementowanego workaroundu
 */

import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { Calendar } from 'lucide-react';

import { Card } from '@/components/Card';

interface TabPlaceholderProps {
  /** Tytuł — co tu będzie (np. „Konfiguracja kolumn listy") */
  title: string;
  /** Krótki opis featura */
  description: string;
  /** Faza w której wpadnie (np. „F12.E.9") */
  phase: string;
  /** Opcjonalnie ikona reprezentująca feature */
  icon?: LucideIcon;
  /** Opcjonalny call-to-action — gdzie user może coś zrobić zamiast czekać */
  fallbackHint?: string;
}

export function TabPlaceholder({
  title,
  description,
  phase,
  icon: Icon = Calendar,
  fallbackHint,
}: TabPlaceholderProps) {
  const { t } = useTranslation();

  return (
    <Card padding="lg" className="text-center" data-testid="tab-placeholder">
      <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/10 text-accent-600">
        <Icon className="h-7 w-7" aria-hidden="true" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-text-primary">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-tertiary">{description}</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-bg-tertiary px-3 py-1 text-xs font-medium text-text-secondary">
        {t('tenant.moduleSettings.placeholder.comingIn')}
        <span className="font-mono font-semibold text-accent-600">{phase}</span>
      </div>
      {fallbackHint && (
        <p className="mx-auto mt-4 max-w-md text-xs text-text-muted">{fallbackHint}</p>
      )}
    </Card>
  );
}
